<template>
  <vs-row :vs-justify="description ? 'space-between' : 'center'" vs-type="flex" vs-w="12">
    <vs-col
      v-if="description"
      class="vs-pagination--mb"
      vs-align="center"
      vs-justify="flex-start"
      vs-lg="6"
      vs-sm="12"
      vs-type="flex"
      vs-xs="12">
      <div class="flex flex-row items-center space-x-2">
        <span>{{ descriptionTitle }}</span>

        <slot v-if="$scopedSlots.pageSizeSelector" name="pageSizeSelector"></slot>

        <ul v-else class="vs-pagination--array">
          <li v-for="(row, index) in descriptionItems" :key="index">
            <span
              :class="[`vs-description-${color}`, { 'vs-pagination--bold': index === indexRows }]"
              :style="styleDescription"
              @click="changeRowMaxItems(index)">
              {{ row }}
            </span>
            <span v-if="index != descriptionItems.length - 1"> , </span>
          </li>
        </ul>

        <span v-if="showDescriptionBody">
          {{ descriptionBody }}
          {{ minRows }} - {{ maxRows }}
          {{ descriptionConnector }}
          {{ sizeArray }}
        </span>
      </div>
    </vs-col>
    <vs-col
      v-if="sizeArray > maxItemsNumber"
      :vs-lg="description ? 6 : 12"
      class="vs-pagination--mb"
      vs-align="center"
      vs-justify="flex-end"
      vs-sm="12"
      vs-type="flex"
      vs-xs="12">
      <div :class="[`vs-pagination-${color}`]" :style="stylePagination" class="con-vs-pagination">
        <nav class="vs-pagination--nav">
          <button
            :class="{ disabled: current <= 1 ? 'disabled' : null }"
            :disabled="current === 1"
            class="vs-pagination--buttons btn-prev-pagination vs-pagination--button-prev"
            @click="prevPage">
            <vs-icon :icon="prevIcon ? prevIcon : defaultPrevIcon" :icon-pack="iconPack"></vs-icon>
          </button>
          <ul class="vs-pagination--ul">
            <li
              v-for="(page, index) in pages"
              :key="index"
              :class="{ 'is-current': page == current }"
              class="item-pagination vs-pagination--li"
              @click="goTo(page)">
              <span>
                {{ page }}
              </span>

              <div class="effect"></div>
            </li>
          </ul>
          <button
            :class="{ disabled: current === total ? 'disabled' : null }"
            :disabled="current === total"
            class="vs-pagination--buttons btn-next-pagination vs-pagination--button-next"
            @click="nextPage">
            <vs-icon :icon="nextIcon ? nextIcon : defaultNextIcon" :icon-pack="iconPack"></vs-icon>
          </button>
        </nav>
      </div>
    </vs-col>
  </vs-row>
</template>
<script>
import _color from "@/assets/utils/color.js"

export default {
  name: "UIPagination",
  props: {
    color: {
      type: String,
      default: "primary",
    },
    sizeArray: {
      type: Number,
      required: false,
    },
    maxItems: {
      type: [Number, String],
      required: false,
    },
    value: {
      type: Number,
      required: true,
    },
    max: {
      type: Number,
      default: 9,
    },
    prevIcon: {
      type: String,
    },
    nextIcon: {
      type: String,
    },
    iconPack: {
      type: String,
      default: "material-icons",
    },
    description: {
      default: false,
      type: Boolean,
    },
    descriptionItems: {
      default: () => [],
      type: Array,
    },
    descriptionTitle: {
      type: String,
      default: "Items per page",
    },
    showDescriptionBody: {
      type: Boolean,
      default: true,
    },
    descriptionBody: {
      type: String,
      default: "Items",
    },
    descriptionConnector: {
      type: String,
      default: "of",
    },
  },
  data: () => ({
    pages: [],
    current: 0,
  }),
  computed: {
    // Most callers pass max-items unbound (`max-items="10"`), so the prop arrives
    // as a string. Comparing or indexing against it without coercion silently fails.
    maxItemsNumber() {
      return Number(this.maxItems) || 0
    },
    // Total *pages*, not items — `sizeArray` is the item count.
    total() {
      if (!this.sizeArray || !this.maxItemsNumber) {
        return 0
      }

      return Math.ceil(this.sizeArray / this.maxItemsNumber)
    },
    minRows() {
      if (!this.sizeArray) {
        return 0
      }

      return (this.current - 1) * this.maxItemsNumber + 1
    },
    maxRows() {
      if (!this.sizeArray) {
        return 0
      }

      return Math.min(this.current * this.maxItemsNumber, this.sizeArray)
    },
    indexRows() {
      return this.descriptionItems.indexOf(this.maxItemsNumber)
    },
    defaultNextIcon() {
      if (this.$vs.rtl) return "chevron_left"
      return "chevron_right"
    },
    defaultPrevIcon() {
      if (this.$vs.rtl) return "chevron_right"
      return "chevron_left"
    },
    stylePagination() {
      let style = {}
      if (!_color.isColor(this.color)) {
        style = {
          "--vs-color-pagination": _color.getColor(this.color),
          "--vs-color-pagination-alpha": _color.getColor(this.color, 0.5),
        }
      }
      return style
    },
    styleDescription() {
      return {
        cursor: "pointer",
      }
    },
  },
  watch: {
    current() {
      this.getPages()
      this.$emit("input", this.current)
      this.$emit("change", this.current)
    },
    total() {
      this.getPages()
    },
    max() {
      this.getPages()
    },
    value(val) {
      const pageNum = val < 1 ? 1 : val <= this.total ? val : this.total
      this.goTo(pageNum)
    },
  },

  mounted() {
    this.current = this.value
    this.getPages()
  },

  methods: {
    changeRowMaxItems(index) {
      this.$emit("changeMaxItems", index)
      this.current = 1
    },
    goTo(page) {
      if (page === "...") {
        return
      }

      this.current = page
    },
    getPages() {
      if (this.total <= this.max) {
        this.pages = this.setPages(1, this.total)
        return
      }

      const even = this.max % 2 === 0 ? 1 : 0
      // Only differs from max / 2 for a max below 4, which nothing passes today —
      // kept so a small `max` still produces a sane range.
      const prevRange = this.total < 6 ? Math.floor(this.max / (this.max / 2)) : Math.floor(this.max / 2)
      const nextRange = this.total - prevRange + 1 + even

      if (this.current >= prevRange && this.current <= nextRange) {
        const start = this.current - prevRange + 2
        const end = this.current + prevRange - 2 - even

        this.pages = [1, "...", ...this.setPages(start, end), "...", this.total]
      } else if (this.total < 6) {
        this.pages = [...this.setPages(1, this.total)]
      } else {
        this.pages = [...this.setPages(1, prevRange), "...", ...this.setPages(nextRange, this.total)]
      }
    },
    setPages(start, end) {
      const pages = []

      for (let page = start > 0 ? start : 1; page <= end; page++) {
        pages.push(page)
      }

      return pages
    },

    nextPage() {
      if (this.current < this.total) {
        this.current++
      }
    },
    prevPage() {
      if (this.current > 1) {
        this.current--
      }
    },
  },
}
</script>
